/**
 * OPDS Feed Parser
 *
 * Parses OPDS 1.2 feeds (Atom-based XML format)
 */

import { XMLParser } from 'fast-xml-parser';
import type {
  OPDSFeed,
  OPDSEntry,
  OPDSLink,
  OPDSAuthor,
  OPDSCategory,
  OPDSParserOptions,
  OPDSFacet,
  OpenSearchDescription,
  OpenSearchUrl,
  OPDSSearchParams,
} from './types.js';

/**
 * Parse OPDS feed from XML string
 */
export function parseOPDSFeed(xml: string, options: OPDSParserOptions = {}): OPDSFeed {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    parseAttributeValue: true,
    trimValues: true,
  });

  const result = parser.parse(xml);

  if (!result.feed) {
    throw new Error('Invalid OPDS feed: missing <feed> element');
  }

  const feed = result.feed;
  const baseUrl = options.baseUrl || '';
  const links = parseLinks(feed.link, baseUrl);

  return {
    id: feed.id || '',
    title: feed.title || '',
    updated: feed.updated || new Date().toISOString(),
    author: parseAuthors(feed.author),
    links,
    entries: parseEntries(feed.entry, baseUrl),
    kind: detectFeedKind(feed),
    ...extractNavigationLinks(links),
    facets: extractFacets(links),
  };
}

/**
 * Detect feed kind from feed structure
 */
function detectFeedKind(feed: any): 'navigation' | 'acquisition' {
  const entries = Array.isArray(feed.entry) ? feed.entry : feed.entry ? [feed.entry] : [];

  // Check if any entry has acquisition links
  for (const entry of entries) {
    const links = Array.isArray(entry.link) ? entry.link : entry.link ? [entry.link] : [];
    const hasAcquisition = links.some((link: any) =>
      link['@_rel']?.includes('opds-spec.org/acquisition')
    );
    if (hasAcquisition) {
      return 'acquisition';
    }
  }

  return 'navigation';
}

/**
 * Parse feed entries
 */
function parseEntries(entries: any, baseUrl: string): OPDSEntry[] {
  if (!entries) return [];

  const entryArray = Array.isArray(entries) ? entries : [entries];

  return entryArray.map((entry) => parseEntry(entry, baseUrl));
}

/**
 * Parse single entry
 */
function parseEntry(entry: any, baseUrl: string): OPDSEntry {
  return {
    id: entry.id || '',
    title: entry.title || '',
    updated: entry.updated || new Date().toISOString(),
    summary: entry.summary || undefined,
    content: entry.content ? (typeof entry.content === 'string' ? entry.content : entry.content['#text']) : undefined,
    author: parseAuthors(entry.author),
    category: parseCategories(entry.category),
    dcterms: parseDublinCore(entry),
    links: parseLinks(entry.link, baseUrl),
  };
}

/**
 * Parse authors
 */
function parseAuthors(authors: any): OPDSAuthor[] | undefined {
  if (!authors) return undefined;

  const authorArray = Array.isArray(authors) ? authors : [authors];

  return authorArray.map((author) => ({
    name: author.name || '',
    uri: author.uri || undefined,
  }));
}

/**
 * Parse categories
 */
function parseCategories(categories: any): OPDSCategory[] | undefined {
  if (!categories) return undefined;

  const categoryArray = Array.isArray(categories) ? categories : [categories];

  return categoryArray.map((category) => ({
    term: category['@_term'] || '',
    label: category['@_label'] || undefined,
    scheme: category['@_scheme'] || undefined,
  }));
}

/**
 * Parse Dublin Core metadata
 */
function parseDublinCore(entry: any): OPDSEntry['dcterms'] {
  const dc: any = {};

  if (entry['dcterms:publisher']) dc.publisher = entry['dcterms:publisher'];
  if (entry['dcterms:language']) dc.language = entry['dcterms:language'];
  if (entry['dcterms:issued']) dc.issued = entry['dcterms:issued'];
  if (entry['dcterms:identifier']) dc.identifier = entry['dcterms:identifier'];

  return Object.keys(dc).length > 0 ? dc : undefined;
}

/**
 * Parse links
 */
function parseLinks(links: any, baseUrl: string): OPDSLink[] {
  if (!links) return [];

  const linkArray = Array.isArray(links) ? links : [links];

  return linkArray.map((link) => parseLink(link, baseUrl));
}

/**
 * Parse single link
 */
function parseLink(link: any, baseUrl: string): OPDSLink {
  const href = link['@_href'] || '';
  let absoluteHref = href;

  // Only convert to absolute URL if baseUrl is provided and href is relative
  if (baseUrl && !href.startsWith('http://') && !href.startsWith('https://')) {
    try {
      absoluteHref = new URL(href, baseUrl).toString();
    } catch {
      // Keep original href if URL parsing fails
      absoluteHref = href;
    }
  }

  const parsed: OPDSLink = {
    href: absoluteHref,
    type: link['@_type'] || undefined,
    rel: link['@_rel'] || undefined,
    title: link['@_title'] || undefined,
  };

  // OPDS-PSE attributes
  if (link['@_pse:count']) {
    parsed.count = parseInt(link['@_pse:count'], 10);
  }
  if (link['@_pse:lastRead']) {
    parsed.lastRead = parseInt(link['@_pse:lastRead'], 10);
  }

  return parsed;
}

/**
 * Check if link is a page streaming link
 */
export function isPageStreamLink(link: OPDSLink): boolean {
  return (
    link.rel === 'http://vaemendis.net/opds-pse/stream' &&
    link.count !== undefined &&
    link.href.includes('{pageNumber}')
  );
}

/**
 * Build page stream URL
 */
export function buildPageStreamUrl(link: OPDSLink, pageNumber: number, maxWidth?: number): string {
  if (!isPageStreamLink(link)) {
    throw new Error('Link is not a page streaming link');
  }

  let url = link.href.replace('{pageNumber}', pageNumber.toString());

  if (maxWidth !== undefined && url.includes('{maxWidth}')) {
    url = url.replace('{maxWidth}', maxWidth.toString());
  } else if (url.includes('{maxWidth}')) {
    // Remove optional maxWidth parameter if not provided
    url = url.replace(/[?&]width=\{maxWidth\}/, '');
    // Also handle other parameter name patterns
    url = url.replace(/[?&]maxWidth=\{maxWidth\}/, '');
    // Remove standalone placeholder
    url = url.replace(/\{maxWidth\}/, '');
  }

  return url;
}

/**
 * Extract navigation links from feed links
 */
function extractNavigationLinks(links: OPDSLink[]): Pick<OPDSFeed, 'searchLink' | 'startLink' | 'upLink' | 'selfLink'> {
  const result: any = {};

  for (const link of links) {
    if (link.rel === 'search' && link.type === 'application/opensearchdescription+xml') {
      result.searchLink = link.href;
    } else if (link.rel === 'start') {
      result.startLink = link.href;
    } else if (link.rel === 'up') {
      result.upLink = link.href;
    } else if (link.rel === 'self') {
      result.selfLink = link.href;
    }
  }

  return result;
}

/**
 * Extract facets from feed links
 */
function extractFacets(links: OPDSLink[]): OPDSFacet[] {
  const facets: OPDSFacet[] = [];

  for (const link of links) {
    if (link.rel === 'http://opds-spec.org/facet' && link.href && link.title) {
      facets.push({
        href: link.href,
        title: link.title,
        // Parse opds:facetGroup and opds:activeFacet from link if available
        // These would need to be extracted during link parsing
      });
    }
  }

  return facets;
}

/**
 * Parse OpenSearch description document
 */
export function parseOpenSearchDescription(xml: string): OpenSearchDescription {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    parseAttributeValue: true,
    trimValues: true,
  });

  const result = parser.parse(xml);

  if (!result.OpenSearchDescription) {
    throw new Error('Invalid OpenSearch document: missing <OpenSearchDescription> element');
  }

  const doc = result.OpenSearchDescription;

  const urls: OpenSearchUrl[] = [];
  const urlArray = Array.isArray(doc.Url) ? doc.Url : doc.Url ? [doc.Url] : [];

  for (const url of urlArray) {
    urls.push({
      type: url['@_type'] || '',
      template: url['@_template'] || '',
      rel: url['@_rel'] || undefined,
    });
  }

  return {
    shortName: doc.ShortName || '',
    description: doc.Description || '',
    url: urls,
  };
}

/**
 * Build search URL from OpenSearch template
 */
export function buildSearchUrl(template: string, params: OPDSSearchParams): string {
  let url = template;

  // Replace search parameters
  if (params.searchTerms !== undefined) {
    url = url.replace('{searchTerms}', encodeURIComponent(params.searchTerms));
  }
  if (params.author !== undefined) {
    url = url.replace('{atom:author}', encodeURIComponent(params.author));
  }
  if (params.title !== undefined) {
    url = url.replace('{atom:title}', encodeURIComponent(params.title));
  }

  // Replace any remaining custom parameters
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && !['searchTerms', 'author', 'title'].includes(key)) {
      url = url.replace(`{${key}}`, encodeURIComponent(value));
    }
  }

  // Remove any unreplaced optional parameters
  url = url.replace(/\{[^}]+\?\}/g, '');

  return url;
}
